import {EmptyStringArgumentError, PerformanceMeasurementError} from "@buildwithflux/core";
import {areWeTestingWithJest} from "@buildwithflux/shared";
import {isPaletteProfilingEnabled} from "helpers/paletteHelpers";

import {FluxLogger} from "./LogConnector";

export interface IPerformanceTrace {
    name: string;
    markName: string;
}

export default class PerformanceLogger {
    private static instance: PerformanceLogger;
    private readonly perfPrefix: string = "FLUX-PERF:";
    private readonly traceNameStartPrefix: string = this.perfPrefix + "TRACE-START:";
    private readonly traceNameStopPrefix: string = this.perfPrefix + "TRACE-STOP:";
    private readonly traceNameMarkPrefix: string = this.perfPrefix + "TRACE-MARK:";
    private readonly checkNamePrefix: string = this.perfPrefix + "CHECK-MARK:";
    private traceCounter: Map<string, number> = new Map<string, number>();

    public static getInstance(): PerformanceLogger {
        if (!PerformanceLogger.instance) {
            PerformanceLogger.instance = new PerformanceLogger();
        }
        return PerformanceLogger.instance;
    }

    private async startPaletteMeasure(markName: string): Promise<void> {
        try {
            const {measure} = await import("@palette.dev/browser");
            measure.start(markName);
        } catch (error) {}
    }

    private async endPaletteMeasure(markName: string): Promise<void> {
        try {
            const {measure} = await import("@palette.dev/browser");
            measure.end(markName);
        } catch (error) {}
    }

    public startPerformanceTrace(traceName: string): IPerformanceTrace {
        if (areWeTestingWithJest()) {
            return {
                name: "noTraceInUnitTests",
                markName: "noTraceInUnitTests",
            } as IPerformanceTrace;
        }
        if (!traceName) {
            throw new EmptyStringArgumentError("startPerformanceTrace", traceName);
        }
        return this.startBrowserPerformanceTrace(traceName);
    }

    public stopPerformanceTrace(trace: IPerformanceTrace): number {
        if (areWeTestingWithJest()) {
            return 0;
        }
        return this.stopBrowserPerformanceTrace(trace);
    }

    public setPerformanceCheckMark(markName: string) {
        if (!areWeTestingWithJest()) {
            performance.mark(this.checkNamePrefix + markName);
        }
    }

    // TODO - The performance.mark and parformance.measure call below is a bit expensive (~3ms),
    // need to come up with a more efficient way to measure performance while not creating more
    // noises to performance.
    private startBrowserPerformanceTrace(traceName: string): IPerformanceTrace {
        const count = this.traceCounter.get(traceName);
        let markName = traceName;
        let newCount = 1;
        if (count) {
            markName = traceName + ":" + count.toString();
            newCount = count + 1;
        }
        this.traceCounter.set(traceName, newCount);

        performance.mark(this.traceNameStartPrefix + markName);

        if (isPaletteProfilingEnabled() && !areWeTestingWithJest()) {
            this.startPaletteMeasure(this.perfPrefix + markName);
        }

        return {
            name: traceName,
            markName,
        } as IPerformanceTrace;
    }

    private stopBrowserPerformanceTrace(trace: IPerformanceTrace): number {
        performance.mark(this.traceNameStopPrefix + trace.markName);
        performance.measure(
            this.traceNameMarkPrefix + trace.markName,
            this.traceNameStartPrefix + trace.markName,
            this.traceNameStopPrefix + trace.markName,
        );

        if (isPaletteProfilingEnabled() && !areWeTestingWithJest()) {
            this.endPaletteMeasure(this.perfPrefix + trace.markName);
        }

        const results = performance.getEntriesByName(this.traceNameMarkPrefix + trace.markName);

        if (!results[0]) {
            FluxLogger.captureError(new PerformanceMeasurementError(this.traceNameStartPrefix + trace.markName));

            return 0;
        }
        return results[0].duration;
    }
}

export function withPerformanceTrace<T>(name: string, innerFunctionToBeTraced: () => T): T {
    const logger = PerformanceLogger.getInstance();
    const trace = logger.startPerformanceTrace(name);

    try {
        return innerFunctionToBeTraced();
    } finally {
        logger.stopPerformanceTrace(trace);
    }
}
